export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isOffline() {
    return this.status === 0;
  }
}

import { localeForCurrency } from './territory';
import {
  configureSessionStorage,
  hydrateSessionTokens,
  ensureSessionHydrated,
  getAccessToken,
  getAdminAccessToken,
  setAccessToken,
  setAdminAccessToken,
  setOnAuthExpired,
  notifyAuthExpired,
  SESSION_KEY,
  ADMIN_SESSION_KEY,
} from './session.js';

export {
  configureSessionStorage,
  hydrateSessionTokens,
  ensureSessionHydrated,
  getAccessToken,
  getAdminAccessToken,
  setAccessToken,
  setAdminAccessToken,
  setOnAuthExpired,
  SESSION_KEY,
  ADMIN_SESSION_KEY,
};

const BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? '';

/** App Store 3.1.1: paid digital goods must not open hosted web checkout on iOS. */
export function allowExternalWebCheckout(platform: string | undefined): boolean {
  return (platform ?? '').trim().toLowerCase() !== 'ios';
}

export function clientPlatformHeader(): Record<string, string> {
  const getPlatform = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform;
  if (typeof getPlatform !== 'function') return {};
  const platform = getPlatform();
  if (!platform) return {};
  return { 'X-Cevop-Client-Platform': platform };
}

function errorFromPayload(payload: unknown, status: number): ApiError {
  const body = payload as {
    error?: string | { code?: string; message?: string; details?: unknown };
    code?: string;
    data?: unknown;
  } | null;
  if (typeof body?.error === 'string') {
    return new ApiError(body.error, status, body.code, body.data);
  }
  if (body?.error && typeof body.error === 'object') {
    return new ApiError(
      body.error.message ?? `Request failed (${status})`,
      status,
      body.error.code ?? body.code,
      body.error.details ?? body.data,
    );
  }
  return new ApiError(`Request failed (${status})`, status);
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; token?: string | null; headers?: Record<string, string> } = {},
): Promise<T> {
  const method = opts.method ?? 'GET';
  const token = opts.token === undefined ? getAccessToken() : opts.token;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...clientPlatformHeader(),
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
    });
  } catch {
    throw new ApiError('Cannot reach the server. Check your connection.', 0);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  try {
    const text = await res.text();
    payload = text && text !== 'undefined' ? JSON.parse(text) : null;
  } catch {
    if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status);
    throw new ApiError('The server sent an unreadable response.', res.status);
  }

  if (!res.ok) {
    if (res.status === 401) {
      if (opts.token === undefined) {
        setAccessToken(null);
        notifyAuthExpired();
      }
      throw errorFromPayload(payload, res.status);
    }
    throw errorFromPayload(payload, res.status);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: { headers?: Record<string, string> }) => request<T>(path, opts),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) =>
    request<T>(path, { method: 'POST', body, ...opts }),
  put: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) =>
    request<T>(path, { method: 'PUT', body, ...opts }),
  patch: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) =>
    request<T>(path, { method: 'PATCH', body, ...opts }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  async upload<T>(path: string, file: Blob, fields?: Record<string, string>): Promise<T> {
    const token = getAccessToken();
    const form = new FormData();
    form.append('file', file);
    if (fields) {
      for (const [key, value] of Object.entries(fields)) form.append(key, value);
    }
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...clientPlatformHeader(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      });
    } catch {
      throw new ApiError('Cannot reach the server. Check your connection.', 0);
    }
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      throw new ApiError(res.ok ? 'The server sent an unreadable response.' : `Request failed (${res.status})`, res.status);
    }
    if (!res.ok) {
      if (res.status === 401) {
        setAccessToken(null);
        notifyAuthExpired();
      }
      throw errorFromPayload(payload, res.status);
    }
    return payload as T;
  },
  async download(path: string): Promise<Blob> {
    const token = getAccessToken();
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...clientPlatformHeader(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {
      throw new ApiError('Cannot reach the server. Check your connection.', 0);
    }
    if (!res.ok) {
      if (res.status === 401) {
        setAccessToken(null);
        notifyAuthExpired();
      }
      let payload: unknown = null;
      try {
        payload = await res.json();
      } catch {
        throw new ApiError(`Request failed (${res.status})`, res.status);
      }
      throw errorFromPayload(payload, res.status);
    }
    return res.blob();
  },
};

export const adminApi = {
  get: <T>(path: string, opts?: { params?: Record<string, string> }) => {
    const url = opts?.params ? `${path}?${new URLSearchParams(opts.params).toString()}` : path;
    return request<T>(url, { token: getAdminAccessToken() });
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, token: getAdminAccessToken() }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body, token: getAdminAccessToken() }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body, token: getAdminAccessToken() }),
  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE', token: getAdminAccessToken() }),
  async download(path: string): Promise<Blob> {
    const token = getAdminAccessToken();
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...clientPlatformHeader(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {
      throw new ApiError('Cannot reach the server. Check your connection.', 0);
    }
    if (!res.ok) {
      if (res.status === 401) {
        setAdminAccessToken(null);
        notifyAuthExpired();
      }
      let payload: unknown = null;
      try {
        payload = await res.json();
      } catch {
        throw new ApiError(`Request failed (${res.status})`, res.status);
      }
      throw errorFromPayload(payload, res.status);
    }
    return res.blob();
  },
};

/** Sentinel when amount is not a finite number — never fabricate ₦0.00. */
export const MONEY_UNAVAILABLE = '—';

export function money(amount: string | number, currency = 'NGN', locale?: string): string {
  const numeric = typeof amount === 'number' ? amount : Number(amount);
  const code = currency.trim().toUpperCase() || 'NGN';
  if (!Number.isFinite(numeric)) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      // Fail loud in tests/dev so corrupt amounts cannot hide as zero.
      throw new TypeError(`Invalid money amount: ${String(amount)}`);
    }
    return MONEY_UNAVAILABLE;
  }
  try {
    return new Intl.NumberFormat(localeForCurrency(code, locale), {
      style: 'currency',
      currency: code,
    }).format(numeric);
  } catch {
    const n = Math.abs(numeric);
    const formatted = n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${numeric < 0 ? '−' : ''}${code} ${formatted}`;
  }
}

export function newIdempotencyKey(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `web-${Date.now()}-${rand}`;
}
